import React, { useState, useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  EditButton,
  Edit,
  SimpleForm,
  TextInput,
  Create,
  Show,
  TabbedShowLayout,
  Tab,
  ShowButton,
  useRecordContext,
  useTranslate,
  Filter,
  FilterLiveSearch,
  DateField,
  EmailField,
  UrlField,
  Button as RaButton,
  TopToolbar,
  useListContext,
  SelectColumnsButton,
  ExportButton,
  useListController,
  ListContextProvider,
  FunctionField,
  useNotify,
  useRefresh,
} from 'react-admin';
import { styled } from '@mui/material/styles';
import {
  TextField as MuiTextField,
  Box,
  Card,
  CardContent,
  Stack,
  Chip,
  Grid,
  Typography,
  Divider, Button as MuiButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import * as XLSX from 'xlsx';
import { Link } from 'react-router-dom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PredictionsIcon from '@mui/icons-material/Analytics';
import API_URL from './config';
import PredictionsDashboard from './components/PredictionsDashboard';

const StyledSimpleForm = styled(SimpleForm)(({ theme }) => ({
  backgroundColor: '#fff',
  padding: '24px',
  borderRadius: '8px',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
  '& .MuiFormControl-root': {
    width: '100%',
    maxWidth: '600px',
    marginBottom: '16px',
  },
  '& .MuiInputBase-root': {
    width: '100%',
  },
}));

// Style the show layout to format fields
const StyledTabbedShowLayout = styled(TabbedShowLayout)(({ theme }) => ({
  '& .RaLabeled-label': {
    display: 'inline-block',
    width: '200px',
    marginRight: '8px',
    fontWeight: 'bold',
    verticalAlign: 'baseline',
    lineHeight: '1.5',
    '&:after': {
      content: '":"',
    },
  },
  '& .RaLabeled-value': {
    display: 'inline-block',
    verticalAlign: 'baseline',
    lineHeight: '1.5',
  },
  '& .RaLabeled-wrapper': {
    marginBottom: '8px',
    display: 'flex',
    alignItems: 'baseline',
    minHeight: '24px',
  },
}));

const BuyerFilter = (props) => {
  const translate = useTranslate();
  return (
    <Filter {...props}>
      <FilterLiveSearch
        source="name"
        label={translate('resources.buyers.fields.name')}
      />
      <FilterLiveSearch
        source="external_id"
        label={translate('resources.buyers.fields.external_id')}
      />
    </Filter>
  );
};

const ExportExcelButton = () => {
  const { data } = useListContext();

  const handleExport = () => {
    if (!data || data.length === 0) return;
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Buyers');
    XLSX.writeFile(workbook, 'buyers.xlsx');
  };

  return (
    <RaButton
      label="Export to Excel"
      onClick={handleExport}
      startIcon={<FileDownloadIcon />}
      sx={{
        backgroundColor: '#E31837',
        color: '#ffffff',
        '&:hover': {
          backgroundColor: '#C41430',
        },
        '& .MuiSvgIcon-root': {
          color: '#ffffff',
        },
      }}
    />
  );
};

const BuyerFilterSidebar = () => {
  const translate = useTranslate();
  const { setFilters, filterValues } = useListContext();
  
  const [filterKey, setFilterKey] = useState(Date.now());

  useEffect(() => {
    const hasAnyFilter = 
      filterValues.name || 
      filterValues.tax_number || 
      filterValues.registration_number ||
      filterValues.city ||
      filterValues.municipality ||
      filterValues.province ||
      filterValues.party_type;
    
    if (!hasAnyFilter && Object.keys(filterValues).length === 0) {
      setFilterKey(Date.now());
    }
  }, [filterValues]);

  const handleResetFilters = () => {
    setFilterKey(Date.now());
    
    requestAnimationFrame(() => {
      const allFilterFields = {
        name: '',
        tax_number: '',
        registration_number: '',
        city: '',
        municipality: '',
        province: '',
        party_type: '',
      };
      
      setFilters(allFilterFields, {});
      
      requestAnimationFrame(() => {
        setFilters({}, {});
      });
    });
  };

  return (
    <Card
      sx={{
        order: -1,
        mr: 2,
        mt: 9,
        minWidth: 250,
        maxWidth: 300,
        boxShadow: 0,
        borderRadius: 2,
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <FilterLiveSearch key={`name-${filterKey}`}
            source="name"
            label={translate('resources.buyers.fields.name')}
          />
          <FilterLiveSearch key={`tax_number-${filterKey}`}
            source="tax_number"
            label={translate('resources.buyers.fields.tax_number')}
          />
          <FilterLiveSearch
            key={`registration_number-${filterKey}`} source="registration_number"
            label={translate('resources.buyers.fields.registration_number')}
          />
          <FilterLiveSearch key={`city-${filterKey}`}
            source="city"
            label={translate('resources.buyers.fields.city')}
          />
          <FilterLiveSearch key={`municipality-${filterKey}`}
            source="municipality"
            label={translate('resources.buyers.fields.municipality')}
          />
          <FilterLiveSearch key={`province-${filterKey}`}
            source="province"
            label={translate('resources.buyers.fields.province')}
          />
          <FilterLiveSearch key={`party_type-${filterKey}`}
            source="party_type"
            label={translate('resources.buyers.fields.party_type')}
          />

          <MuiButton 
            variant="outlined" 
            size="small" 
            onClick={handleResetFilters}
            sx={{ mt: 3, mb: 1 }}
            fullWidth
          >
            Wyczyść filtry
          </MuiButton>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ListActions = () => {
  return (
    <TopToolbar>
      <ExportButton
        maxResults={100000}
        icon={<FileDownloadIcon />}
        sx={{
          backgroundColor: '#E31837',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: '#C41430',
          },
          '& .MuiSvgIcon-root': {
            color: '#ffffff',
          },
        }}
      />
      <ExportExcelButton />
    </TopToolbar>
  );
};

export const BoolChip = ({ value }) => {
  const translate = useTranslate();
  if (value && value !== '0' && value !== 0 && value !== '' && value !== null) {
    return (
      <Chip label={translate('ra.boolean.true')} color="success" size="small" />
    );
  }
  return (
    <Chip label={translate('ra.boolean.false')} color="default" size="small" />
  );
};

export const BuyerList = (props) => {
  const translate = useTranslate();
  return (
    <List
      {...props}
      perPage={25}
      sort={{ field: 'created_at', order: 'DESC' }}
      actions={<ListActions />}
      aside={<BuyerFilterSidebar />}
    >
      <Datagrid rowClick="show" bulkActionButtons={false}>
        <TextField
          source="id"
          label={translate('resources.buyers.fields.id')}
        />
        <TextField
          source="name"
          label={translate('resources.buyers.fields.name')}
        />
        <TextField
          source="tax_number"
          label={translate('resources.buyers.fields.tax_number')}
        />
        <TextField
          source="registration_number"
          label={translate('resources.buyers.fields.registration_number')}
        />

        <TextField
          source="city"
          label={translate('resources.buyers.fields.city')}
        />
        <TextField
          source="municipality"
          label={translate('resources.buyers.fields.municipality')}
        />
        <TextField
          source="province"
          label={translate('resources.buyers.fields.province')}
        />
        <TextField
          source="party_type"
          label={translate('resources.buyers.fields.party_type')}
        />
        <NumberField
          source="total_tenders"
          label={translate('resources.buyers.fields.total_tenders')}
        />
        <NumberField
          source="active_tenders"
          label={translate('resources.buyers.fields.active_tenders')}
        />
        <DateField
          source="updated_at"
          label={translate('resources.buyers.fields.updated_at')}
          showTime
        />
        <ShowButton label={translate('ra.action.show')} />
      </Datagrid>
    </List>
  );
};

const TenderShowButton = () => {
  const record = useRecordContext();
  if (!record || !record.id) return null;
  return (
    <RaButton
      component={Link}
      to={`/tenders/${record.id}/show`}
      label="Pokaż"
      color="primary"
      size="small"
    />
  );
};

const PredictTendersButton = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const [predictionData, setPredictionData] = useState(null);

  const handlePredict = async () => {
    if (!record || !record.id) {
      notify(translate('resources.buyers.actions.predict.error.no_id'), {
        type: 'error',
      });
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('access_token');

    try {
      const response = await fetch(
        `${API_URL}/predict-tenders/?ordering_party_id=${record.id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            translate('resources.buyers.actions.predict.error.failed')
        );
      }

      const result = await response.json();
      setPredictionData(result);

      if (result.prediction_success) {
        notify(
          translate('resources.buyers.actions.predict.success', {
            name: result.ordering_party_name || record.name,
            count: result.predictions?.length || 0,
          }),
          { type: 'success' }
        );
      } else {
        notify(
          translate('resources.buyers.actions.predict.error.message', {
            error: result.prediction_error || 'Unknown error',
          }),
          { type: 'error' }
        );
      }
    } catch (error) {
      notify(
        translate('resources.buyers.actions.predict.error.message', {
          error: error.message,
        }),
        { type: 'error' }
      );
      setPredictionData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <RaButton
          label={translate(
            'resources.buyers.actions.predict.label',
            'Prognozuj zamówienia'
          )}
          onClick={handlePredict}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={20} /> : <PredictionsIcon />
          }
          sx={{
            backgroundColor: '#E31837',
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#C41430',
            },
            '&:disabled': {
              backgroundColor: '#cccccc',
              color: '#666666',
            },
            '& .MuiSvgIcon-root': {
              color: '#ffffff',
            },
          }}
        />
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {predictionData && !loading && (
        <PredictionsDashboard predictionData={predictionData} />
      )}

      {!predictionData && !loading && (
        <Alert severity="info">
          {translate(
            'resources.buyers.predictions.no_data',
            'Kliknij "Prognozuj zamówienia" aby wygenerować prognozę zamówień okresowych.'
          )}
        </Alert>
      )}
    </Box>
  );
};

// Workflow Status Field Component
const WorkflowStatusField = ({ source, label }) => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const status = record[source];

  const getStatusConfig = (status) => {
    switch (status) {
      case 'not_started':
        return { color: 'default', label: 'Not Started' };
      case 'in_progress':
        return { color: 'primary', label: 'In Progress' };
      case 'completed':
        return { color: 'success', label: 'Completed' };
      case 'failed':
        return { color: 'error', label: 'Failed' };
      case 'partial':
        return { color: 'warning', label: 'Bidders Completed' };
      case 'bidders_completed':
        return { color: 'warning', label: 'Bidders Completed' };
      case 'cancelled':
        return { color: 'error', label: 'Cancelled' };
      default:
        return { color: 'default', label: status || 'Unknown' };
    }
  };

  const config = getStatusConfig(status);

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  );
};

// Archived Status Field Component
const ArchivedStatusField = ({ source, label }) => {
  const record = useRecordContext();
  const translate = useTranslate();

  if (!record) return null;

  const isArchived = record[source];

  return (
    <Chip
      label={isArchived ? 'Zarchiwizowany' : 'Aktywny'}
      color={isArchived ? 'default' : 'success'}
      size="small"
      variant="outlined"
    />
  );
};

const BuyerTendersList = () => {
  const record = useRecordContext();
  const translate = useTranslate();
  const resource = 'tenders';

  const listContext = useListController({
    resource,
    filter: { buyer_id: record?.id },
    sort: { field: 'publication_date', order: 'DESC' },
    perPage: 25,
    disableSyncWithLocation: true,
  });

  if (!record) return null;
  if (listContext.isLoading) return null;
  if (listContext.error) return <div>Error loading tenders</div>;

  return (
    <ListContextProvider value={listContext}>
      <div style={{ marginTop: '1em' }}>
        <Datagrid bulkActionButtons={false}>
          <TextField
            source="id"
            label={translate('resources.tenders.fields.id')}
          />
          <TextField
            source="source"
            label={translate('resources.tenders.fields.source')}
          />
          <TextField
            source="bzp_number"
            label={translate('resources.tenders.fields.bzp_number')}
          />
          <DateField
            source="publication_date"
            label={translate('resources.tenders.fields.publication_date')}
            showTime
          />
          <TextField
            source="title"
            label={translate('resources.tenders.fields.title')}
          />
          <WorkflowStatusField
            source="status"
            label={translate('resources.tenders.fields.status')}
          />
          <NumberField
            source="details.bid_bond"
            label={translate('resources.tenders.fields.bid_bond')}
            options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
          />
          <NumberField
            source="details.nwu"
            label={translate('resources.tenders.fields.nwu')}
            options={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
          />
          <ArchivedStatusField
            source="is_archived"
            label={translate('resources.tenders.fields.is_archived')}
          />
          <TenderShowButton />
        </Datagrid>
      </div>
    </ListContextProvider>
  );
};

export const BuyerShow = (props) => {
  const translate = useTranslate();
  return (
    <Show {...props}>
      <StyledTabbedShowLayout>
        <Tab label={translate('resources.buyers.tabs.basic_info')}>
          <Grid container spacing={4}>
            {/* Basic Info Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate(
                    'resources.buyers.sections.basic_info',
                    'Informacje podstawowe'
                  )}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={1}>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.id')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="id" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.name')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="name" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.external_id')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="external_id" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.source')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="source" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.party_type')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="party_type" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.business_activity')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="business_activity" />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Contact Info Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate(
                    'resources.buyers.sections.contact_info',
                    'Dane kontaktowe'
                  )}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={1}>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.email')}
                  </Grid>
                  <Grid item xs={7}>
                    <EmailField source="email" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.website')}
                  </Grid>
                  <Grid item xs={7}>
                    <UrlField source="website" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.street')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="street" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.postal_code')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="postal_code" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.city')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="city" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.municipality')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="municipality" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.province')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="province" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.country')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="country" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.nuts3_code')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="nuts3_code" />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Company Info Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate(
                    'resources.buyers.sections.company_info',
                    'Informacje o firmie'
                  )}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={1}>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.tax_number')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="tax_number" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.registration_number')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="registration_number" />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Statistics Column */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate('resources.buyers.sections.stats', 'Statystyki')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={1}>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.total_tenders')}
                  </Grid>
                  <Grid item xs={7}>
                    <NumberField source="total_tenders" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.active_tenders')}
                  </Grid>
                  <Grid item xs={7}>
                    <NumberField source="active_tenders" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.created_at')}
                  </Grid>
                  <Grid item xs={7}>
                    <DateField source="created_at" showTime />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.updated_at')}
                  </Grid>
                  <Grid item xs={7}>
                    <DateField source="updated_at" showTime />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Tab>
        <Tab label={translate('resources.buyers.tabs.tenders')}>
          <BuyerTendersList />
        </Tab>
        <Tab
          label={translate('resources.buyers.tabs.predictions', 'Predykcje')}
        >
          <PredictTendersButton />
        </Tab>
      </StyledTabbedShowLayout>
    </Show>
  );
};

export const BuyerEdit = (props) => {
  const translate = useTranslate();
  return (
    <Edit {...props}>
      <SimpleForm>
        <Box sx={{ backgroundColor: 'white', p: 3, borderRadius: 1 }}>
          <Grid container spacing={4}>
            {/* Basic Info Column - Read Only */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate(
                    'resources.buyers.sections.basic_info',
                    'Informacje podstawowe'
                  )}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={1}>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.id')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="id" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.name')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="name" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.external_id')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="external_id" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.source')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="source" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.party_type')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="party_type" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.business_activity')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="business_activity" />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Contact Info Column - Editable */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate(
                    'resources.buyers.sections.contact_info',
                    'Dane kontaktowe'
                  )}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextInput
                      source="email"
                      label={translate('resources.buyers.fields.email')}
                      type="email"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      source="website"
                      label={translate('resources.buyers.fields.website')}
                      type="url"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextInput
                      source="street"
                      label={translate('resources.buyers.fields.street')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      source="postal_code"
                      label={translate('resources.buyers.fields.postal_code')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      source="city"
                      label={translate('resources.buyers.fields.city')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      source="municipality"
                      label={translate('resources.buyers.fields.municipality')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      source="province"
                      label={translate('resources.buyers.fields.province')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      source="country"
                      label={translate('resources.buyers.fields.country')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextInput
                      source="nuts3_code"
                      label={translate('resources.buyers.fields.nuts3_code')}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Company Info Column - Read Only */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate(
                    'resources.buyers.sections.company_info',
                    'Informacje o firmie'
                  )}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={1}>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.tax_number')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="tax_number" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.registration_number')}
                  </Grid>
                  <Grid item xs={7}>
                    <TextField source="registration_number" />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Statistics Column - Read Only */}
            <Grid item xs={12} md={6}>
              <Box sx={{ maxWidth: 700, mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {translate('resources.buyers.sections.stats', 'Statystyki')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={1}>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.total_tenders')}
                  </Grid>
                  <Grid item xs={7}>
                    <NumberField source="total_tenders" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.active_tenders')}
                  </Grid>
                  <Grid item xs={7}>
                    <NumberField source="active_tenders" />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.created_at')}
                  </Grid>
                  <Grid item xs={7}>
                    <DateField source="created_at" showTime />
                  </Grid>
                  <Grid item xs={5} sx={{ color: 'text.secondary' }}>
                    {translate('resources.buyers.fields.updated_at')}
                  </Grid>
                  <Grid item xs={7}>
                    <DateField source="updated_at" showTime />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </SimpleForm>
    </Edit>
  );
};

export const BuyerCreate = (props) => {
  const translate = useTranslate();
  return (
    <Create {...props}>
      <StyledSimpleForm>
        <TextInput
          source="name"
          label={translate('resources.buyers.fields.name')}
          fullWidth
        />
        <TextInput
          source="external_id"
          label={translate('resources.buyers.fields.external_id')}
          fullWidth
        />
        <TextInput
          source="party_type"
          label={translate('resources.buyers.fields.party_type')}
          fullWidth
        />
        <TextInput
          source="business_activity"
          label={translate('resources.buyers.fields.business_activity')}
          fullWidth
        />
        <TextInput
          source="source"
          label={translate('resources.buyers.fields.source')}
          fullWidth
        />
        <TextInput
          source="email"
          label={translate('resources.buyers.fields.email')}
          type="email"
          fullWidth
        />
        <TextInput
          source="website"
          label={translate('resources.buyers.fields.website')}
          type="url"
          fullWidth
        />
        <TextInput
          source="street"
          label={translate('resources.buyers.fields.street')}
          fullWidth
        />
        <TextInput
          source="postal_code"
          label={translate('resources.buyers.fields.postal_code')}
          fullWidth
        />
        <TextInput
          source="city"
          label={translate('resources.buyers.fields.city')}
          fullWidth
        />
        <TextInput
          source="municipality"
          label={translate('resources.buyers.fields.municipality')}
          fullWidth
        />
        <TextInput
          source="province"
          label={translate('resources.buyers.fields.province')}
          fullWidth
        />
        <TextInput
          source="country"
          label={translate('resources.buyers.fields.country')}
          fullWidth
        />
        <TextInput
          source="nuts3_code"
          label={translate('resources.buyers.fields.nuts3_code')}
          fullWidth
        />
      </StyledSimpleForm>
    </Create>
  );
};
